/**
 * /sectors/:key — blog-style area page.
 *
 * Layout (desktop):
 *   - Hero strip: sector icon + label + tagline + KPI row
 *   - 66/33 split: LEFT (featured tracker + latest snapshot)  |  RIGHT (editorial cards)
 *
 * Reads /api/sectors/:key/page (one aggregated payload).
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import PublicNav from "../layouts/PublicNav";
import SeoHead from "../components/SeoHead";
import useSiteContent from "../lib/useSiteContent";
import { useLanguage } from "../hooks/useLanguage";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, BarChart, Bar } from "recharts";

export default function SectorAreaPage() {
  const { key } = useParams();
  const [page, setPage] = useState(null);
  const [trackerData, setTrackerData] = useState(null);
  const [err, setErr] = useState(null);
  const cms = useSiteContent();
  const { lang } = useLanguage();

  useEffect(() => {
    setPage(null); setErr(null); setTrackerData(null);
    api.get(`/sectors/${key}/page`)
      .then((r) => {
        setPage(r.data);
        if (r.data.featured_tracker) {
          api.get(`/trackers/${r.data.featured_tracker.id}/data`)
             .then((d) => setTrackerData(d.data))
             .catch(() => setTrackerData({ series: [] }));
        }
      })
      .catch((e) => setErr(e.response?.data?.detail || "Sector not found"));
  }, [key]);

  if (err) return <Wrap><div className="text-red-600 p-12">{err}</div></Wrap>;
  if (!page) return <Wrap><div className="text-neutral-500 p-12">Loading…</div></Wrap>;

  const s = page.sector;
  const sLabel = (lang === "ur" && (s.label_ur || "").trim()) ? s.label_ur : s.label;
  const snap = page.latest_snapshot;
  const snapOptions = (snap?.options || []).filter((o) => !o.exclude_from_trendline);
  const snapBars = snap ? Object.entries(snap.overall || {}).map(([k, v]) => ({
    name: (snapOptions.find((o) => o.key === k)?.text) || k, value: Number(v) || 0,
  })) : [];

  return (
    <Wrap>
      <SeoHead
        title={s.seo_title || `${s.label} — Pakistan consumer insights | oodel.`}
        description={s.seo_description || s.tagline || `Live trackers, snapshots and editorial about ${s.label.toLowerCase()} in Pakistan from oodel.`}
        canonical={`${(cms.seo_canonical_base || (typeof window !== "undefined" ? window.location.origin : "")).replace(/\/$/, "")}/sectors/${s.key}`}
        image={s.hero_image_url || cms.seo_default_image_url || undefined}
        type="article"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": s.label,
          "url": `${(cms.seo_canonical_base || (typeof window !== "undefined" ? window.location.origin : "")).replace(/\/$/, "")}/sectors/${s.key}`,
          "description": s.seo_description || s.tagline,
          "isPartOf": { "@type": "WebSite", "name": cms.brand_name || "oodel.", "url": cms.seo_canonical_base || (typeof window !== "undefined" ? window.location.origin : "") },
        }}
      />
      {/* Hero strip */}
      <section className="border-b-2 border-black" style={{ background: s.color ? `${s.color}10` : undefined }}>
        <div className="max-w-[1400px] mx-auto px-6 py-12">
          <div className="overline mb-2 text-neutral-500">SECTOR</div>
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <span className="text-5xl" aria-hidden="true">{s.icon}</span>
            <h1 className="text-5xl md:text-6xl font-black tracking-tighter" data-testid="sector-title">{sLabel}</h1>
          </div>
          <p className="text-xl md:text-2xl text-neutral-700 max-w-3xl">{s.tagline}</p>
          {s.description && <p className="text-base text-neutral-600 max-w-3xl mt-4">{s.description}</p>}
          {s.seo_intro_paragraph && (
            <p className="text-sm text-neutral-700 max-w-3xl mt-4 leading-relaxed border-l-4 border-mango pl-4" data-testid="sector-seo-intro">
              {s.seo_intro_paragraph}
            </p>
          )}
        </div>
      </section>

      <div className="max-w-[1400px] mx-auto px-6 py-12 grid grid-cols-1 lg:grid-cols-3 gap-10">
        {/* LEFT 66% — featured tracker + latest snapshot */}
        <div className="lg:col-span-2 space-y-10">
          {page.featured_tracker ? (
            <section data-testid="sector-featured-tracker">
              <div className="overline mb-2 text-mango">LIVE TRACKER</div>
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-3">{page.featured_tracker.name}</h2>
              <p className="text-neutral-600 text-sm mb-5">{page.featured_tracker.description}</p>
              <div className="bg-white border-2 border-black p-5 h-72">
                {trackerData?.series?.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={trackerData.series}>
                      <XAxis dataKey="date" stroke="#737373" fontSize={11} />
                      <YAxis stroke="#737373" fontSize={11} />
                      <Tooltip />
                      {(trackerData.options || []).slice(0, 5).map((o, i) => (
                        <Line key={o.key} type="monotone" dataKey={o.key} stroke={["#16A34A", "#dc2626", "#2563eb", "#f59e0b", "#a855f7"][i] || "#737373"} dot={false} strokeWidth={2} />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-neutral-400 italic text-sm">Not enough data yet to plot a trend.</div>
                )}
              </div>
              <Link to={`/trackers/${page.featured_tracker.id}`} className="mt-3 inline-block text-sm font-mono text-mango hover:underline">View full tracker →</Link>
            </section>
          ) : (
            <div className="border-2 border-dashed border-neutral-300 p-10 text-center text-neutral-500">
              No tracker live yet in {sLabel}. Become a Founding Patron to be first when one launches.
            </div>
          )}

          {snap && (
            <section data-testid="sector-latest-snapshot">
              <div className="overline mb-2 text-mango">LATEST SNAPSHOT</div>
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-3">{snap.question_text}</h2>
              <div className="bg-white border-2 border-black p-5 h-72">
                {snapBars.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={snapBars}>
                      <XAxis dataKey="name" stroke="#737373" fontSize={10} />
                      <YAxis stroke="#737373" fontSize={11} />
                      <Tooltip />
                      <Bar dataKey="value" fill="#16A34A" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-neutral-400 italic text-sm">No snapshot data yet.</div>
                )}
              </div>
              <div className="mt-2 text-xs text-neutral-500">n = {snap.n_sample} · {snap.date}</div>
            </section>
          )}
        </div>

        {/* RIGHT 33% — editorial */}
        <aside className="lg:col-span-1" data-testid="sector-editorial">
          <div className="overline mb-3 text-neutral-500">EDITORIAL · {sLabel.toUpperCase()}</div>
          {page.articles.length === 0 && (
            <div className="text-sm text-neutral-500 italic border border-dashed border-neutral-300 p-4">No articles published yet.</div>
          )}
          <div className="space-y-5">
            {page.articles.map((a) => {
              const excerpt = (a.body || "").replace(/<[^>]+>/g, " ").trim().slice(0, 160);
              const readMin = Math.max(2, Math.round((a.body || "").length / 1500));
              return (
                <Link key={a.id} to={`/articles/${a.slug || a.id}`} className="block group border-b border-neutral-200 pb-5 hover:bg-neutral-50 px-2 -mx-2">
                  {a.cover_image && (
                    <div className="aspect-video bg-neutral-100 mb-3 overflow-hidden">
                      <img src={a.cover_image} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    </div>
                  )}
                  <div className="text-xs text-neutral-500 mb-1">{(a.created_at || "").slice(0, 10)} · {readMin} min read{a.audio_url ? " · 🎧 audio" : ""}{a.ai_generated ? " · AI-assisted" : ""}</div>
                  <h3 className="text-lg font-bold leading-tight group-hover:text-mango transition-colors">{a.title}</h3>
                  {excerpt && <p className="text-sm text-neutral-600 mt-2 line-clamp-3">{excerpt}…</p>}
                </Link>
              );
            })}
          </div>
        </aside>
      </div>
    </Wrap>
  );
}

function Wrap({ children }) {
  return (
    <div className="min-h-screen bg-white themable-public">
      <PublicNav />
      {children}
    </div>
  );
}
